import React, { useState, useEffect, useRef } from 'react';
import { AVATAR_ASSETS, SKIN_TONES, HAIR_COLORS, CLOTHING_COLORS } from '../constants/AvatarAssets';

export interface AvatarConfig {
    background: string;
    body: string;
    skinTone: string;
    face: string;
    detail: string;
    clothing: string;
    clothingColor: string;
    hair: string;
    hairColor: string;
    accessory: string;
    flag: string;
}

interface Props {
    config: AvatarConfig;
    size?: number;
    className?: string;
}

const DEFAULT_CONFIG: AvatarConfig = {
    background: 'none',
    body: 'light',
    skinTone: '#FFE0BD',
    face: 'normal',
    detail: 'none',
    clothing: 'basic',
    clothingColor: '#4f46e5',
    hair: 'bald',
    hairColor: '#2C222B',
    accessory: 'none',
    flag: 'none'
};

const AvatarRenderer: React.FC<Props> = ({ config, size = 200, className = "" }) => {
    // Ensure config is valid and has all properties
    const safeConfig = config ? { ...DEFAULT_CONFIG, ...config } : DEFAULT_CONFIG;
    
    const [displayConfig, setDisplayConfig] = useState<AvatarConfig>(safeConfig);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const lastConfigRef = useRef<AvatarConfig>(safeConfig);

    useEffect(() => {
        // If config changed, preload images before switching
        const currentSafe = config ? { ...DEFAULT_CONFIG, ...config } : DEFAULT_CONFIG;
        if (JSON.stringify(currentSafe) !== JSON.stringify(lastConfigRef.current)) {
            setIsTransitioning(true);
            preloadImages(currentSafe).then(() => {
                setDisplayConfig(currentSafe);
                lastConfigRef.current = currentSafe;
                setIsTransitioning(false);
            });
        }
    }, [config]);

    const preloadImages = async (conf: AvatarConfig) => {
        const urls = [
            getAsset('backgrounds', conf.background)?.url,
            getAsset('bodies', conf.body)?.url,
            getAsset('face', conf.face)?.url,
            getAsset('details', conf.detail)?.url,
            getAsset('clothing', conf.clothing)?.url,
            getAsset('hair', conf.hair)?.url,
            getAsset('accessories', conf.accessory)?.url,
        ].filter(Boolean) as string[];

        const promises = urls.map(url => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = url;
                img.onload = resolve;
                img.onerror = resolve; // Continue anyway
            });
        });

        await Promise.all(promises);
    };

    const getAsset = (type: keyof typeof AVATAR_ASSETS, id: string) => {
        return (AVATAR_ASSETS[type] as any[]).find(a => a.id === id) || (AVATAR_ASSETS[type] as any[])[0];
    };

    const current = displayConfig;
    const bg = getAsset('backgrounds', current.background);
    const body = getAsset('bodies', current.body);
    const face = getAsset('face', current.face);
    const detail = getAsset('details', current.detail);
    const clothing = getAsset('clothing', current.clothing);
    const hair = getAsset('hair', current.hair);
    const accessory = getAsset('accessories', current.accessory);
    const flag = getAsset('flags', current.flag);

    const layerStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        imageRendering: 'pixelated',
        transition: 'opacity 0.2s ease-in-out',
        opacity: isTransitioning ? 0.7 : 1, // Subtle dim during transition but keeps old view
    };

    return (
        <div
            className={`relative overflow-hidden rounded-[28px] bg-slate-100 dark:bg-slate-800 ${className}`}
            style={{
                width: size,
                height: size,
            }}
        >
            {/* 1. Background */}
            {bg?.url && (
                <img
                    src={bg.url}
                    alt=""
                    style={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: '100%', height: '100%',
                        objectFit: 'cover',
                    }}
                />
            )}

            <div className="relative w-full h-full">
                {/* 2. Body */}
                {body?.url && <img src={body.url} style={layerStyle} alt="" />}

                {/* 3. Face */}
                {face?.url && <img src={face.url} style={layerStyle} alt="" />}

                {/* 4. Details (Cleft Lip, Freckles, etc.) */}
                {detail?.url && <img src={detail.url} style={layerStyle} alt="" />}

                {/* 5. Clothing */}
                {clothing?.url && <img src={clothing.url} style={layerStyle} alt="" />}

                {/* 6. Hair */}
                {hair?.url && <img src={hair.url} style={layerStyle} alt="" />}

                {/* 7. Accessories */}
                {accessory?.url && <img src={accessory.url} style={layerStyle} alt="" />}
            </div>

            {/* 9. Flag */}
            {flag.emoji && (
                <div className="absolute top-2 right-2 text-lg drop-shadow-md z-10">
                    {flag.emoji}
                </div>
            )}
        </div>
    );
};

export default AvatarRenderer;
